import type { RefObject } from "react";
import {
  ArrowRight,
  CheckCircle,
  Download,
  Target,
  TrendingUp,
  Zap,
} from "lucide-react";
import type { CampaignReportResponse } from "../../types";
import { BarChart } from "../../components/BarChart";
import { DonutChart } from "../../components/DonutChart";
import type { CompactLayoutViewModel } from "./compactLayoutData";

interface CompactLayoutHeaderProps {
  totalDays: number;
  campaignCount: number;
  exportingSlide: string | null;
  onExportAllSlides: () => Promise<void>;
}

interface CompactLayoutSlideProps {
  slideRef: RefObject<HTMLDivElement | null>;
  reportType: string;
  summary: CampaignReportResponse["summary"];
  viewModel: CompactLayoutViewModel;
  onExportSlide: (
    slideRef: RefObject<HTMLDivElement | null>,
    slideName: string,
  ) => Promise<void>;
}

const getSourceLabel = (reportType: string) =>
  reportType === "search" ? "WMC" : "Walmart Display";

export function CompactLayoutHeader({
  totalDays,
  campaignCount,
  exportingSlide,
  onExportAllSlides,
}: CompactLayoutHeaderProps) {
  return (
    <div className="max-w-[1920px] mx-auto mb-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Executive Performance Overview
          </h1>
          <p className="text-base text-gray-600 dark:text-gray-400 mt-2">
            2-Slide Strategic Summary • {totalDays} days • {campaignCount} campaigns
          </p>
        </div>
        <button
          onClick={onExportAllSlides}
          disabled={exportingSlide !== null}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-semibold shadow-lg flex items-center gap-2 text-base"
        >
          <Download className="w-5 h-5" />
          Export Both Slides
        </button>
      </div>
    </div>
  );
}

export function PerformancePulseSlide({
  slideRef,
  reportType,
  summary,
  viewModel,
  onExportSlide,
}: CompactLayoutSlideProps) {
  return (
    <div
      ref={slideRef}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
      style={{ aspectRatio: "16/9", minHeight: "600px" }}
    >
      <div className="p-12 h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-1">
              Performance Pulse
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-base">
              Last {summary.total_days} Days • {summary.campaign_count} Campaigns •{" "}
              {reportType === "search" ? "Walmart SP" : "Walmart Display"}
            </p>
          </div>
          <button
            onClick={() => onExportSlide(slideRef, "performance-pulse")}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 no-export"
          >
            <Download className="w-6 h-6" />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-5 border border-blue-200 dark:border-blue-700 shadow-sm">
            <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-1.5">
              Sales
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              ${(viewModel.totalSales / 1000).toFixed(1)}k
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 rounded-xl p-5 border border-red-200 dark:border-red-700 shadow-sm">
            <div className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-1.5">
              Spend
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              ${(viewModel.totalSpend / 1000).toFixed(1)}k
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl p-5 border border-green-200 dark:border-green-700 shadow-sm">
            <div className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-1.5">
              ROAS
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {viewModel.avgROAS.toFixed(2)}x
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-5 border border-purple-200 dark:border-purple-700 shadow-sm">
            <div className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-1.5">
              Trend
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              {viewModel.salesGrowthRate > 0 ? (
                <>
                  <TrendingUp className="w-6 h-6 text-green-500" />+
                  {viewModel.salesGrowthRate.toFixed(1)}%
                </>
              ) : (
                <>{viewModel.salesGrowthRate.toFixed(1)}%</>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-5 mb-5 flex-1">
          <div className="col-span-3 flex flex-col">
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-5 border border-gray-200 dark:border-gray-700 h-full flex flex-col shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                {summary.total_days}-Day Sales vs Spend
              </h3>
              <div className="flex-1 min-h-0 mb-3">
                <BarChart
                  labels={viewModel.dailyLabels}
                  datasets={viewModel.dailyChartData}
                  stacked={false}
                  horizontal={false}
                  showLegend={true}
                />
              </div>
              <div className="flex gap-2 text-xs flex-wrap">
                <span className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200 rounded-full font-medium">
                  Avg Daily Sales: ${(viewModel.avgDailySales / 1000).toFixed(1)}k
                </span>
                <span className="px-3 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-200 rounded-full font-medium">
                  Avg Daily Spend: ${(viewModel.avgDailySpend / 1000).toFixed(1)}k
                </span>
                <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200 rounded-full font-medium">
                  Trend: {viewModel.salesGrowthRate > 0 ? "+" : ""}
                  {viewModel.salesGrowthRate.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="col-span-2 flex flex-col">
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-5 border border-gray-200 dark:border-gray-700 h-full flex flex-col shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Sales Mix
              </h3>
              <div className="flex-1 min-h-0 flex items-center justify-center">
                <DonutChart data={viewModel.salesMixData} />
              </div>
              <p className="text-xs italic text-gray-600 dark:text-gray-400 mt-2 text-center">
                {viewModel.salesMixData[0]?.brand_name} leads at{" "}
                {viewModel.salesMixData[0]?.percentage.toFixed(0)}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-blue-300 dark:border-blue-700 shadow-sm">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-blue-900 dark:text-blue-300 text-sm">
                  Insight:
                </span>
                <span className="text-gray-800 dark:text-gray-200 ml-2 text-sm">
                  We're growing efficiently—
                  <strong>
                    {viewModel.salesGrowthRate > 0 ? "uptrend" : "stable"}
                  </strong>{" "}
                  in sales with <strong>{viewModel.avgROAS.toFixed(2)}x</strong> ROAS.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-indigo-900 dark:text-indigo-300 text-sm">
                  Analysis:
                </span>
                <span className="text-gray-800 dark:text-gray-200 ml-2 text-sm">
                  Sales are{" "}
                  {viewModel.salesGrowthRate > 0
                    ? "compounding day-over-day"
                    : "consistent"}{" "}
                  while spend stays disciplined; mix concentrated in{" "}
                  <strong>{viewModel.salesMixData[0]?.brand_name}</strong>, which is
                  also the most efficient brand.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-green-900 dark:text-green-300 text-sm">
                  Action (this week):
                </span>
                <ul className="text-gray-800 dark:text-gray-200 ml-2 mt-1.5 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Scale momentum windows:</strong> raise daily caps on
                      strongest days/times.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Guardrails on efficiency:</strong> keep ROAS floors;
                      nudge bids only on keywords with consistent conversion.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Coverage check:</strong> confirm inventory & PDP
                      readiness on top sellers to protect the run-rate.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          Source: {getSourceLabel(reportType)} • USD • {summary.total_days} days •{" "}
          {summary.campaign_count} campaigns
        </div>
      </div>
    </div>
  );
}

export function PortfolioStrengthSlide({
  slideRef,
  reportType,
  summary,
  viewModel,
  onExportSlide,
}: CompactLayoutSlideProps) {
  return (
    <div
      ref={slideRef}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
      style={{ aspectRatio: "16/9", minHeight: "600px" }}
    >
      <div className="p-12 h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-1">
              Portfolio Strength & Winners
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-base">
              ROAS Performance & Top Campaigns
            </p>
          </div>
          <button
            onClick={() => onExportSlide(slideRef, "portfolio-strength")}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 no-export"
          >
            <Download className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-5 flex-shrink-0">
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
              ROAS by Brand
            </h3>
            <div className="h-40">
              <BarChart
                labels={viewModel.roasLabels}
                datasets={[
                  {
                    label: "ROAS",
                    data: viewModel.roasValues,
                    backgroundColor: viewModel.roasValues.map((value) =>
                      value >= 5
                        ? "rgba(34, 197, 94, 0.9)"
                        : value >= 3
                          ? "rgba(59, 130, 246, 0.9)"
                          : "rgba(251, 191, 36, 0.9)",
                    ),
                  },
                ]}
                horizontal={true}
                showLegend={false}
              />
            </div>
            <p className="text-xs italic text-gray-600 dark:text-gray-400 mt-2 text-center">
              {viewModel.brandROAS.filter((brand) => brand.roas >= 3.0).length} brands
              are <strong>well above</strong> a 3.0x benchmark;{" "}
              {viewModel.brandROAS[0]?.brand} is the growth engine.
            </p>
          </div>
        </div>

        <div className="mb-5 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
            Top 3 Winning Campaigns
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {viewModel.topCampaigns.map((campaign, index) => {
              const campaignROAS = parseFloat(campaign.roas);
              const campaignSales = parseFloat(campaign.total_sales);
              const campaignSpend = parseFloat(campaign.total_spend);

              return (
                <div
                  key={campaign.campaign_name}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-300 dark:border-green-700 relative shadow-sm"
                >
                  <div className="absolute top-3 right-3 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                    #{index + 1}
                  </div>
                  <div className="pr-10">
                    <h4
                      className="text-sm font-bold text-gray-900 dark:text-white truncate mb-3"
                      title={campaign.campaign_name}
                    >
                      {campaign.campaign_name.length > 22
                        ? `${campaign.campaign_name.substring(0, 22)}...`
                        : campaign.campaign_name}
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                          ${(campaignSales / 1000).toFixed(1)}k
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          on ${(campaignSpend / 1000).toFixed(1)}k spend
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-green-300 dark:border-green-700">
                        <div>
                          <div className="text-xl font-bold text-green-700 dark:text-green-300">
                            {campaignROAS.toFixed(2)}x
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            ROAS
                          </div>
                        </div>
                        <div>
                          <div className="text-xl font-bold text-gray-900 dark:text-white">
                            {campaign.total_units.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            units
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-300 dark:border-green-700 shadow-sm flex-1 min-h-0 overflow-auto">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-green-900 dark:text-green-300 text-sm">
                  Insight:
                </span>
                <span className="text-gray-800 dark:text-gray-200 ml-2 text-sm">
                  <strong>High-ROAS campaigns are scalable</strong> today; opportunity
                  is shifting budget toward them.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-emerald-900 dark:text-emerald-300 text-sm">
                  Analysis:
                </span>
                <span className="text-gray-800 dark:text-gray-200 ml-2 text-sm">
                  {viewModel.brandROAS[0]?.brand} and {viewModel.brandROAS[1]?.brand}{" "}
                  deliver{" "}
                  <strong>
                    &gt;
                    {Math.min(
                      viewModel.brandROAS[0]?.roas || 0,
                      viewModel.brandROAS[1]?.roas || 0,
                    ).toFixed(0)}
                    x
                  </strong>{" "}
                  efficiency;
                  {viewModel.brandROAS.length > 2 &&
                    ` ${viewModel.brandROAS[2]?.brand} sits at ${viewModel.brandROAS[2]?.roas.toFixed(2)}x.`}{" "}
                  Redirecting dollars from lower ROAS to higher ROAS{" "}
                  <strong>nearly doubles</strong> sales per dollar.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-green-900 dark:text-green-300 text-sm">
                  Action (reallocation plan):
                </span>
                <ul className="text-gray-800 dark:text-gray-200 ml-2 mt-1.5 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Move 20–30%</strong> of lower-performing budget to{" "}
                      {viewModel.brandROAS[0]?.brand} winners; <strong>+10%</strong>{" "}
                      to {viewModel.brandROAS[1]?.brand} lines.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Expected lift (illustrative):</strong> shifting $5k from{" "}
                      {viewModel.brandROAS[viewModel.brandROAS.length - 1]?.roas.toFixed(1)}x
                      to {viewModel.brandROAS[0]?.roas.toFixed(1)}x yields ≈ $
                      {(
                        (viewModel.brandROAS[0]?.roas -
                          viewModel.brandROAS[viewModel.brandROAS.length - 1]?.roas) *
                        5000
                      ).toFixed(0)}{" "}
                      incremental sales.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Scale safely:</strong> day-part where CVR is highest; add
                      proven KW variants from SQR; keep creative/value props
                      consistent on detail pages.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          Source: {getSourceLabel(reportType)} • USD • {summary.total_days} days •{" "}
          {summary.campaign_count} campaigns
        </div>
      </div>
    </div>
  );
}
